import { getBackendUrl } from "api/common";
import { queryFnPOST } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { InternalMessagesFormField } from "features/CenterContent/RoleContent/InternalMessage/schema/formSchema";
import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import {
  internalMessagesFormToRequest,
  toInternalMessageWithEvaluationFormRequest,
} from "features/CenterContent/RoleContent/InternalMessage/schema/transformers";
import { INTERNAL_EVENTS_QUERY_KEY } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/EventsOverviewButtonAndModal";
import { TrainInternalMessagesQueryKey } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/useTrainInternalMessages";
import {
  VisibilityResponse,
  visibilitySchemaResponse,
} from "shared/form/visibilitySchema";

export type ResponseWithVisibility = {
  uuid: string;
  visibility: VisibilityResponse;
  incidentId?: string; // If the response has incidentId, then include it in the internal message as well
};

// If you're debugging with this function, have a look at internalMessages.test.ts for help
export const createInternalMessage = async (
  internalMessagesFormField: InternalMessagesFormField,
  relatedEventResponse: ResponseWithVisibility,
  relatedEventPrevUuid?: string,
  evaluationForm?: { url: string },
) => {
  if (
    !relatedEventResponse ||
    !relatedEventResponse.visibility ||
    !relatedEventResponse.uuid ||
    !internalMessagesFormField
  )
    return;
  const visibilityForm = visibilitySchemaResponse.parse(
    relatedEventResponse.visibility,
  );
  const internalMessage = evaluationForm
    ? toInternalMessageWithEvaluationFormRequest(
        internalMessagesFormField[0].uuid,
        evaluationForm,
        internalMessagesFormField[0].contexts ?? [],
        visibilityForm,
        relatedEventResponse.incidentId,
        relatedEventResponse.uuid,
      )
    : internalMessagesFormToRequest(internalMessagesFormField, visibilityForm, {
        incidentId: relatedEventResponse.incidentId,
        relatedEventUuid: relatedEventResponse.uuid,
        relatedEventPrevUuid,
      });
  if (!internalMessage) return;
  const path = internalMessage.uuid
    ? `${internalMessage.uuid}/change`
    : `create`;
  await queryFnPOST({
    url: `${getBackendUrl()}/global-information/${path}`,
    body: internalMessage,
    signal: undefined,
  }).then(() => {
    // No need to invalidate trainInternalMessagesQueryKey here
    // since that is an internal message not related to an event.
    // We can only make internal messages related to an event in this function.
    void queryClient.invalidateQueries({
      queryKey: [INTERNAL_EVENTS_QUERY_KEY],
    });
  });
};

export const createInternalMessageWithTimedVisibility = async (
  internalMessages: InternalMessageResponse[] | undefined,
  relatedEventResponse: ResponseWithVisibility | void,
) => {
  if (!internalMessages || !relatedEventResponse) return;
  await Promise.all(
    internalMessages.map((message) => {
      const internalMessageFormField: InternalMessagesFormField = [
        {
          uuid: message.uuid,
          contexts: message.customTexts[0].contexts,
          message: message.customTexts[0].texts.NOB?.description ?? "",
        },
      ];
      return createInternalMessage(
        internalMessageFormField,
        relatedEventResponse,
        undefined,
        message.evaluationForm,
      );
    }),
  );
};

// If you're debugging with this function, have a look at internalMessages.test.ts for help
export const depublishInternalMessage = async (
  internalMessages: InternalMessageResponse[] | undefined,
) => {
  if (!internalMessages || internalMessages.length === 0) return;
  const latestInternalMessage = internalMessages[0];
  await queryFnPOST({
    url: `${getBackendUrl()}/operational-information/${latestInternalMessage.uuid}/depublish`,
    signal: undefined,
  }).then(() => {
    const train = latestInternalMessage.relevantTrain;
    if (train) {
      const trainInternalMessagesQueryKey = TrainInternalMessagesQueryKey(
        train?.nominalDate,
        train?.identifier,
        train?.countryCode,
      );
      void queryClient.invalidateQueries({
        queryKey: trainInternalMessagesQueryKey,
      });
    }
    void queryClient.invalidateQueries({
      queryKey: [INTERNAL_EVENTS_QUERY_KEY],
    });
  });
};
